import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@repo/ui";
import { Protected, useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useEffect, useMemo, useState } from "react";

const STAGES = [
  { value: "new", label: "New lead" },
  { value: "discovery", label: "Discovery" },
  { value: "proposal", label: "Proposal" },
  { value: "negotiation", label: "Negotiation" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
] as const;
const SOURCES = [
  { value: "referral", label: "Referral" },
  { value: "website", label: "Website" },
  { value: "linkedin", label: "LinkedIn" },
] as const;

export default function LeadsPage() {
  return (
    <Protected redirectTo="/login">
      <Content />
    </Protected>
  );
}

function Content() {
  const authFetch = useAuthenticatedFetch();
  const [q, setQ] = useState("");
  const [stage, setStage] = useState<string>("");
  type Lead = {
    id: number;
    title: string;
    stage: string;
    source?: string;
    amount_cents?: number;
    expected_close_date?: string | null;
    notes?: string | null;
  };
  const [data, setData] = useState<Lead[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Lead | null>(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (stage) params.set("stage", stage);
    const res = await authFetch(`/api/leads?${params.toString()}`);
    setData((await res.json()) as Lead[]);
  };
  useEffect(() => {
    load();
  }, [q, stage]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const payload: Partial<Lead> & { title: string; stage: string } = {
      title: String(form.get("title") || ""),
      amount_cents: Math.round(Number(form.get("amount")) * 100) || 0,
      stage: String(form.get("stage") || "new"),
      source: form.get("source") ? String(form.get("source")) : undefined,
      expected_close_date: form.get("expected_close_date")
        ? String(form.get("expected_close_date"))
        : undefined,
      notes: form.get("notes") ? String(form.get("notes")) : undefined,
    };
    if (editing?.id) {
      await authFetch(`/api/leads/${editing.id}`, { method: "PUT", body: JSON.stringify(payload) });
    } else {
      await authFetch(`/api/leads`, { method: "POST", body: JSON.stringify(payload) });
    }
    setOpen(false);
    setEditing(null);
    await load();
  };

  const moveStage = async (lead: Lead, nextStage: string) => {
    await authFetch(`/api/leads/${lead.id}`, {
      method: "PUT",
      body: JSON.stringify({ ...lead, stage: nextStage }),
    });
    await load();
  };

  const remove = async (id: number) => {
    if (!confirm("Delete lead?")) return;
    await authFetch(`/api/leads/${id}`, { method: "DELETE" });
    await load();
  };

  const rows = useMemo<Lead[]>(() => data, [data]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Search leads"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All</SelectItem>
            {STAGES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditing(null)}>New Lead</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Lead" : "New Lead"}</DialogTitle>
            </DialogHeader>
            <form className="space-y-3" onSubmit={onSubmit}>
              <div>
                <Label htmlFor="title">Title</Label>
                <Input id="title" name="title" defaultValue={editing?.title || ""} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Stage</Label>
                  <Select name="stage" defaultValue={editing?.stage || "new"}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Source</Label>
                  <Select name="source" defaultValue={editing?.source || undefined}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="amount">Amount ($)</Label>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    step="0.01"
                    defaultValue={
                      editing ? (Number(editing.amount_cents || 0) / 100).toString() : ""
                    }
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="expected_close_date">Expected Close</Label>
                <Input
                  id="expected_close_date"
                  name="expected_close_date"
                  type="date"
                  defaultValue={editing?.expected_close_date || ""}
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" name="notes" defaultValue={editing?.notes || ""} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        <Button asChild variant="outline">
          <a href="/api/export/leads.csv">Export</a>
        </Button>
      </div>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list">List</TabsTrigger>
          <TabsTrigger value="kanban">Kanban</TabsTrigger>
        </TabsList>
        <TabsContent value="list">
          <Card className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="w-[160px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.title}</TableCell>
                    <TableCell className="capitalize">{r.stage}</TableCell>
                    <TableCell className="capitalize">{r.source}</TableCell>
                    <TableCell>{formatCurrency(Number(r.amount_cents || 0) / 100)}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <NotesButton entityType="lead" entityId={r.id} />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditing(r);
                          setOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => remove(r.id)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        <TabsContent value="kanban">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {STAGES.map((s) => (
              <Card key={s.value} className="p-3 space-y-3">
                <div className="font-semibold">{s.label}</div>
                <div className="space-y-3">
                  {rows
                    .filter((r) => r.stage === s.value)
                    .map((r) => (
                      <div key={r.id} className="rounded-md border p-3 bg-background">
                        <div className="font-medium">{r.title}</div>
                        <div className="text-sm text-muted-foreground">
                          {formatCurrency(Number(r.amount_cents || 0) / 100)}
                        </div>
                        <div className="flex gap-2 mt-2">
                          <NotesButton entityType="lead" entityId={r.id} />
                          {prevStage(s.value) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => moveStage(r, prevStage(s.value)!)}
                            >
                              Back
                            </Button>
                          )}
                          {nextStage(s.value) && (
                            <Button size="sm" onClick={() => moveStage(r, nextStage(s.value)!)}>
                              Next
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function nextStage(s: string) {
  const order = STAGES.map((x) => x.value);
  const i = order.findIndex((v) => v === s);
  if (i === -1 || i === order.length - 1) return null;
  return order[i + 1];
}
function prevStage(s: string) {
  const order = STAGES.map((x) => x.value);
  const i = order.findIndex((v) => v === s);
  if (i <= 0) return null;
  return order[i - 1];
}
function formatCurrency(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function NotesButton({
  entityType,
  entityId,
}: {
  entityType: "contact" | "lead";
  entityId: number;
}) {
  const authFetch = useAuthenticatedFetch();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Array<{ id: number; content: string; created_at: string }>>(
    []
  );
  const [content, setContent] = useState("");

  const load = async () => {
    const res = await authFetch(`/api/notes?entityType=${entityType}&entityId=${entityId}`);
    const json = (await res.json()) as Array<{ id: number; content: string; created_at: string }>;
    setItems(json);
  };

  const add = async () => {
    if (!content.trim()) return;
    await authFetch(`/api/notes`, {
      method: "POST",
      body: JSON.stringify({ entity_type: entityType, entity_id: entityId, content }),
    });
    setContent("");
    await load();
  };

  const remove = async (id: number) => {
    await authFetch(`/api/notes/${id}`, { method: "DELETE" });
    await load();
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          Notes
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Notes</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {items.map((n) => (
            <div key={n.id} className="flex items-start justify-between gap-3 border rounded p-2">
              <div>
                <div className="text-sm whitespace-pre-wrap">{n.content}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(n.id)}>
                Delete
              </Button>
            </div>
          ))}
          <div className="space-y-2">
            <Label htmlFor={`note-${entityType}-${entityId}`}>Add note</Label>
            <Textarea
              id={`note-${entityType}-${entityId}`}
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <div className="flex justify-end">
              <Button onClick={add}>Add</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
